// @ts-strict-ignore
import Descriptions from 'antd/lib/descriptions';
import * as React from 'react';
import {
    appleReleaseTypeForReleaseMethod,
    releaseMethodForAppleReleaseType,
    releaseSettingsNeedPhasedReleaseWarning,
} from '../../helpers';
import { automationDisplayNameForAutomationId, displayVersionReleaseType } from '../../helpers/displayHelpers';
import {
    AppleAppStoreDefaultSettings,
    AppleAppStoreData,
    AppStoreType,
    AutomationType,
    ReleaseStepSubmissionData,
    ReleaseStepType,
    SettingType,
} from '../../types';
import {
    createAppSettingsAutomationsRouteWithAutomation,
    createAppSettingsRouteWithSettingType,
} from '../../helpers/routeHelpers';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { PhasedReleaseScheduleChart } from '../appHome/widgets/LiveReleaseChartWidget';
import { DEFAULT_ROLLOUT_SEQUENCE } from '../../constants';
import Space from 'antd/lib/space';
import { WarningFilled, CheckCircleFilled, ThunderboltFilled } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import Text from 'antd/lib/typography/Text';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import { useAppPlatform } from '@/utils/hooks/helpers';
import { Tooltip } from 'antd/lib';
import { RootState } from '@/reducers/reducers';
import { selectIsAutomationEnabledForReleaseType, selectRelease, selectReleaseStep } from '@/selectors/selectors';
import { useSelector } from 'react-redux';
import { AgeRatingSummary } from '@/types/ageRating';

interface AppleAppStoreReleaseSettingsDescriptionsProps extends ReleaseComponentProps {
    appDefaultSettings?: AppleAppStoreDefaultSettings;
    appStoreReleaseSettings?: AppleAppStoreDefaultSettings;
    appStoreData?: AppleAppStoreData;
    isRollback: boolean;
    isRunwayReleaseAutomationInactiveBecauseOfApple: boolean;
    willBeReleasedByRunway: boolean;
    labelStyle?: React.CSSProperties;
    contentStyle?: React.CSSProperties;
    colon?: boolean;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    releaseStepType?: ReleaseStepType;
    autoStagedRolloutAutomationId: Nullable<string>;
    ageRating?: Nullable<AgeRatingSummary>;
}

const RESET_SUMMARY_RATING_NOTE =
    'Runway’s integration with App Store Connect does not interact with the “Reset iOS App Store Summary Rating” setting.';

const AppleAppStoreReleaseSettingsDescriptions: React.FC<AppleAppStoreReleaseSettingsDescriptionsProps> = (
    props: AppleAppStoreReleaseSettingsDescriptionsProps,
) => {
    const {
        releaseStepType,
        appStoreData,
        appStoreReleaseSettings,
        willBeReleasedByRunway,
        isRunwayReleaseAutomationInactiveBecauseOfApple,
    } = props;

    const platform = useAppPlatform(props);
    const release = useSelector((state: RootState) => selectRelease(state, props.releaseId));
    const releaseStep =
        releaseStepType &&
        useSelector((state: RootState) => selectReleaseStep(state, props.releaseId, releaseStepType));
    const stepData = releaseStep?.data as ReleaseStepSubmissionData;

    const rolloutCadenceAutomationId = props.autoStagedRolloutAutomationId;
    const isRolloutCadenceAutomationEnabled = useSelector((state: RootState) =>
        selectIsAutomationEnabledForReleaseType(state, props.appId, rolloutCadenceAutomationId, release?.type),
    );

    let isPhasedRelease = false;
    let releaseType = null;
    let releaseMethod = null;
    let earliestReleaseDate = null;

    if (appStoreData || appStoreReleaseSettings) {
        isPhasedRelease =
            appStoreData ? !!appStoreData.phasedRelease
            : appStoreReleaseSettings ? (appStoreReleaseSettings as AppleAppStoreDefaultSettings).phasedRelease
            : false;
        releaseType =
            appStoreData ? appStoreData.versionReviewSubmissionItem?.releaseType
            : appStoreReleaseSettings ?
                appleReleaseTypeForReleaseMethod(
                    (appStoreReleaseSettings as AppleAppStoreDefaultSettings).releaseMethod,
                )
            :   null;
        releaseMethod =
            appStoreData?.versionReviewSubmissionItem ?
                releaseMethodForAppleReleaseType(appStoreData.versionReviewSubmissionItem.releaseType)
            : appStoreReleaseSettings ? (appStoreReleaseSettings as AppleAppStoreDefaultSettings).releaseMethod
            : null;
    }

    earliestReleaseDate = appStoreData ? appStoreData.versionReviewSubmissionItem?.earliestReleaseDate : null;

    const usesEncryption =
        appStoreReleaseSettings?.exportComplianceInfoUsesEncryption ??
        props.appDefaultSettings?.exportComplianceInfoUsesEncryption;

    let phasedReleaseText =
        isPhasedRelease ? 'On (release will be phased over 7 days)' : 'Off (release update to all users immediately)';

    const phasedReleaseSettingIsDifferent = !!appStoreData?.phasedRelease != appStoreReleaseSettings?.phasedRelease;
    const releaseMethodSettingIsDifferent = releaseMethod != appStoreReleaseSettings?.releaseMethod;

    // warn if app release phased settings differ from app default phased settings
    const needsPhasedReleaseWarning = releaseSettingsNeedPhasedReleaseWarning(
        platform,
        props.isRollback,
        props.appDefaultSettings,
        appStoreReleaseSettings,
    );

    const GetAppStoreSettingsIsDifferentTooltip = () => {
        if (releaseStepType !== ReleaseStepType.Submission) {
            return null;
        }

        let tooltipText = null;
        if (release?.submittedAt || stepData?.submittedAt) {
            tooltipText =
                'The values in App Store Connect are different from your configured settings in Runway. Runway cannot update App Store Connect settings after the app update has been submitted for review.';
        } else if (release?.setManualReleaseTypeForAutoRelease || stepData?.setManualReleaseTypeForAutoRelease) {
            tooltipText =
                'Runway updated these values in App Store Connect once to match your configured settings, but they were manually changed again. To avoid conflicts, Runway will not re-attempt to update.';
        } else {
            tooltipText =
                'The values in App Store Connect are different from those stored in your default App Store Connect settings. We will try to apply your configured settings within 5 - 10 minutes.';
        }

        return (
            <Tooltip title={tooltipText}>
                <WarningFilled style={{ color: runwayColors.statusYellow, marginLeft: 8 }} />
            </Tooltip>
        );
    };

    return (
        <Descriptions colon={props.colon} column={1} labelStyle={props.labelStyle} contentStyle={props.contentStyle}>
            <Descriptions.Item label="Version release">
                {releaseType ?
                    <Space direction="vertical">
                        <div>
                            {displayVersionReleaseType(releaseType, earliestReleaseDate)}
                            {releaseMethodSettingIsDifferent && GetAppStoreSettingsIsDifferentTooltip()}
                        </div>
                        {isRunwayReleaseAutomationInactiveBecauseOfApple && (
                            <Space align="start">
                                <WarningFilled style={{ color: runwayColors.statusYellow }} />
                                <div>
                                    <Text style={{ color: runwayColors.statusYellow, fontWeight: 600 }}>
                                        App Store Connect override:{' '}
                                    </Text>
                                    <Text style={{ color: antGrey.grey7 }}>
                                        Runway is unable to perform your scheduled release automation because the
                                        version release setting is not set to Manual. Change this setting to Manual to
                                        allow Runway to release automatically as expected.
                                    </Text>
                                </div>
                            </Space>
                        )}
                        {willBeReleasedByRunway && !isRunwayReleaseAutomationInactiveBecauseOfApple && (
                            <Space align="start">
                                <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />
                                <Text style={{ color: antGrey.grey7 }}>
                                    Keep this set to Manual in App Store Connect in order for Runway to run your release
                                    automation.
                                </Text>
                            </Space>
                        )}
                    </Space>
                :   <Text type="secondary">Not set </Text>}
            </Descriptions.Item>
            <Descriptions.Item label="Phased release">
                <Space direction="vertical">
                    <div>
                        {phasedReleaseText ? phasedReleaseText : <Text type="secondary">Not set</Text>}
                        {phasedReleaseSettingIsDifferent && GetAppStoreSettingsIsDifferentTooltip()}
                    </div>
                    {needsPhasedReleaseWarning && (
                        <Space align="start">
                            <WarningFilled style={{ color: runwayColors.statusYellow }} />
                            <Text style={{ color: antGrey.grey7 }}>
                                Phased release is off for this release because it&apos;s a rollback.
                            </Text>
                        </Space>
                    )}
                </Space>
            </Descriptions.Item>
            {(isPhasedRelease || !isRolloutCadenceAutomationEnabled) && (
                <Descriptions.Item label="Phased release schedule">
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {isPhasedRelease && <PhasedReleaseScheduleChart daysToPercent={DEFAULT_ROLLOUT_SEQUENCE} />}
                        {!isRolloutCadenceAutomationEnabled && (
                            <Space align="start">
                                <ThunderboltFilled style={{ color: antGrey.grey6, marginTop: 4 }} />
                                <Text type="secondary">
                                    Rollout cadence is not configured. &quot;
                                    {automationDisplayNameForAutomationId(
                                        AutomationType.AutoStagedRollout,
                                        AppStoreType.Apple,
                                    )}
                                    &quot; (automation) is <b>disabled</b>, so Runway will not manage this
                                    release&apos;s phased release. You can enable and manage options from{' '}
                                    <Link
                                        to={
                                            rolloutCadenceAutomationId ?
                                                createAppSettingsAutomationsRouteWithAutomation(
                                                    props.orgId,
                                                    props.appId,
                                                    rolloutCadenceAutomationId,
                                                )
                                            :   createAppSettingsRouteWithSettingType(
                                                    props.orgId,
                                                    props.appId,
                                                    SettingType.Automations,
                                                )
                                        }
                                        style={{ color: runwayColors.primary }}
                                    >
                                        the automation&apos;s settings
                                    </Link>
                                    .
                                </Text>
                            </Space>
                        )}
                    </Space>
                </Descriptions.Item>
            )}
            <Descriptions.Item label="Export compliance information">
                {usesEncryption == null ?
                    <Text type="secondary">Not set</Text>
                : usesEncryption ?
                    'Yes, my app uses encryption'
                :   'No, my app doesn’t use non-exempt encryption'}
            </Descriptions.Item>
            {appStoreData?.versionReviewSubmissionItem && (
                <Descriptions.Item label="Copyright">
                    {appStoreData.versionReviewSubmissionItem.copyright ?
                        appStoreData.versionReviewSubmissionItem.copyright
                    :   <Space align="start">
                            <WarningFilled style={{ color: runwayColors.statusYellow }} />
                            <Text style={{ color: antGrey.grey7 }}>
                                Not set. App Store Connect requires copyright text to submit for review.
                            </Text>
                        </Space>
                    }
                </Descriptions.Item>
            )}
            {props.ageRating && (
                <Descriptions.Item label="Age rating">
                    <AgeRatingValue ageRating={props.ageRating} />
                </Descriptions.Item>
            )}
            <Descriptions.Item>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {RESET_SUMMARY_RATING_NOTE}
                </Text>
            </Descriptions.Item>
        </Descriptions>
    );
};

const AgeRatingValue: React.FC<{ ageRating: AgeRatingSummary }> = ({ ageRating }) => {
    const answers = ageRating.answers ?? [];
    if (answers.length === 0) {
        return <Text type="secondary">Not set</Text>;
    }

    return (
        <Tooltip
            overlayStyle={{ maxWidth: 400 }}
            title={answers.map(({ question, answer }) => (
                <div key={question}>
                    {question}: {answer}
                </div>
            ))}
        >
            <span
                style={{
                    cursor: 'help',
                    textDecorationLine: 'underline',
                    textDecorationStyle: 'dotted',
                    textUnderlineOffset: 3,
                }}
            >
                {answers.length} {answers.length === 1 ? 'answer' : 'answers'} populated
            </span>
        </Tooltip>
    );
};

export default AppleAppStoreReleaseSettingsDescriptions;
